import { useState } from "react";
import { Link } from "react-router-dom";
import { formatDateRu, formatTimeRu } from "../../../utils/appointmentLabels";
import ClientPortalAccessSection from "./ClientPortalAccessSection";
import ClientMedicalSection from "./ClientMedicalSection";
import ClientNotesSection from "./ClientNotesSection";
import { ClientInfoSection } from "./ClientProfileSections";
import aa from "../AdminPortal/adminApp.module.css";
import s from "../clinic.module.css";
import { Button, EmptyState } from "../../ui";

const COMP_STATUS = {
  pending: "На рассмотрении",
  approved: "Допущен",
  rejected: "Отклонён",
  cancelled: "Отмена",
};

function OverviewFold({ id, title, preview, open, onToggle, children, action, icon, tone = "default" }) {
  return (
    <section
      className={`${aa.clientSection} ${open ? aa.clientSectionOpen : ""} ${aa[`clientSection_${tone}`] || ""}`}
    >
      <button
        type="button"
        className={aa.clientSectionToggle}
        aria-expanded={open}
        aria-controls={id}
        onClick={onToggle}
      >
        {icon ? (
          <span className={`${aa.clientSectionIcon} ${aa[`clientSectionIcon_${tone}`] || ""}`}>
            {icon}
          </span>
        ) : null}
        <span className={aa.clientSectionToggleMain}>
          <h3 className={aa.clientSectionTitle}>{title}</h3>
          {preview ? (
            <span className={aa.clientSectionPreview}>{preview}</span>
          ) : null}
        </span>
        {action ? (
          <span
            className={aa.clientSectionInlineAction}
            onClick={(e) => {
              e.stopPropagation();
              action.onClick();
            }}
            role="presentation"
          >
            {action.label}
          </span>
        ) : null}
        <span className={aa.clientSectionChevron} aria-hidden />
      </button>
      {open ? (
        <div id={id} className={aa.clientSectionBody}>
          {children}
        </div>
      ) : null}
    </section>
  );
}

function ClientOverviewApp({
  patientId,
  patient,
  profile,
  edit,
  setEdit,
  onSave,
  subdomain,
  newPassword,
  setNewPassword,
  onSavePortalEmail,
  onSetPortalPassword,
  savingPortalEmail,
  settingPortalPassword,
  editingInfo,
  onToggleEdit,
  onAddToCompetition,
  inFold = false,
}) {
  const [openId, setOpenId] = useState("groups");
  const [editingMed, setEditingMed] = useState(false);
  const groups = profile?.groups || [];
  const individual = profile?.individual_sessions || [];
  const competitions = profile?.competitions || [];

  const toggle = (id) => setOpenId((prev) => (prev === id ? null : id));

  const groupsPreview =
    groups.length > 0
      ? groups
          .slice(0, 2)
          .map((g) => g.name)
          .join(", ")
      : "не в группах";

  return (
    <div className={aa.clientSections}>
      <div className={aa.clientSectionGroup}>
        <OverviewFold
          id="client-groups"
          title="Группы"
          preview={groupsPreview}
          open={openId === "groups"}
          onToggle={() => toggle("groups")}
          icon="👥"
          tone="blue"
        >
          {groups.length === 0 ? (
            <EmptyState
              title="Не в группах"
              description="Зачислите в разделе «Группы»"
            />
          ) : (
            <ul className={aa.clientOverviewList}>
              {groups.map((g) => (
                <li key={g.id}>
                  <Link
                    to={`/manage/app/groups?group=${g.id}`}
                    className={aa.clientOverviewLink}
                  >
                    <strong>{g.name}</strong>
                    <span>
                      {[g.sport_direction, g.trainer_name].filter(Boolean).join(" · ")}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
          <Link to="/manage/app/groups" className={aa.clientOverviewFootLink}>
            Открыть группы
          </Link>
        </OverviewFold>

        <OverviewFold
          id="client-individual"
          title="Индивидуальные"
          preview={
            individual.length
              ? `${individual.length} занятий`
              : "нет запланированных"
          }
          open={openId === "individual"}
          onToggle={() => toggle("individual")}
          icon="🎯"
          tone="purple"
        >
          {individual.length === 0 ? (
            <p className={aa.clientOverviewMuted}>
              Создайте в календаре — формат «Индивидуальная».
            </p>
          ) : (
            <ul className={aa.clientOverviewList}>
              {individual.slice(0, 5).map((sess) => (
                <li key={sess.id}>
                  <Link
                    to={`/manage/app/schedule?session=${sess.id}`}
                    className={aa.clientOverviewLink}
                  >
                    <strong>
                      {formatDateRu(sess.start_datetime, {
                        day: "numeric",
                        month: "short",
                      })}{" "}
                      {formatTimeRu(sess.start_datetime)}
                    </strong>
                    <span>
                      {[sess.trainer_name, sess.branch_name].filter(Boolean).join(" · ")}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
          <Link
            to={`/manage/app/schedule?create=individual&patient=${patientId}`}
            className={aa.clientOverviewFootLink}
          >
            Запланировать
          </Link>
        </OverviewFold>

        <OverviewFold
          id="client-competitions"
          title="Соревнования"
          preview={
            competitions.length
              ? `${competitions.length} заявок`
              : "без заявок"
          }
          open={openId === "competitions"}
          onToggle={() => toggle("competitions")}
          icon="🏆"
          tone="gold"
        >
          {competitions.length === 0 ? (
            <p className={aa.clientOverviewMuted}>
              Заявка из личного кабинета или списка участников события.
            </p>
          ) : (
            <ul className={aa.clientOverviewList}>
              {competitions.map((c) => (
                <li key={c.id} className={aa.clientOverviewCompRow}>
                  <div>
                    <strong>{c.session?.title || "Соревнование"}</strong>
                    {c.session?.start_datetime ? (
                      <span className={aa.clientOverviewMuted}>
                        {formatDateRu(c.session.start_datetime, {
                          day: "numeric",
                          month: "long",
                        })}
                      </span>
                    ) : null}
                  </div>
                  <span className={aa.clientOverviewStatus}>
                    {COMP_STATUS[c.status] || c.status}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {onAddToCompetition ? (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={onAddToCompetition}
            >
              Добавить на соревнование
            </Button>
          ) : null}
        </OverviewFold>

        <OverviewFold
          id="client-info"
          title="Контакты"
          preview={patient?.phone || patient?.email || "заполнить"}
          open={openId === "info"}
          onToggle={() => toggle("info")}
          icon="📇"
          tone="teal"
          action={
            editingInfo
              ? undefined
              : {
                  label: "Изменить",
                  onClick: () => {
                    if (inFold) setOpenId("info");
                    onToggleEdit();
                  },
                }
          }
        >
          <ClientInfoSection
            patient={patient}
            edit={edit}
            setEdit={setEdit}
            onSave={onSave}
            editing={editingInfo}
            onToggleEdit={onToggleEdit}
            embedded
            inFold={inFold}
          />
        </OverviewFold>

        <OverviewFold
          id="client-medical"
          title="Мед. допуск"
          preview={edit?.medical_clearance_expires_at ? "указан" : "не указан"}
          open={openId === "medical"}
          onToggle={() => toggle("medical")}
          icon="🩺"
          tone="green"
          action={
            inFold && !editingMed
              ? {
                  label: "Обновить",
                  onClick: () => {
                    setOpenId("medical");
                    setEditingMed(true);
                  },
                }
              : undefined
          }
        >
          <ClientMedicalSection
            edit={edit}
            setEdit={setEdit}
            onSave={onSave}
            editing={editingMed}
            onToggleEdit={() => setEditingMed((v) => !v)}
            embedded
            inFold={inFold}
          />
        </OverviewFold>

        <OverviewFold
          id="client-portal"
          title="Личный кабинет"
          preview={edit?.email?.includes("@patient.internal") ? "нужен email" : "настроен"}
          open={openId === "portal"}
          onToggle={() => toggle("portal")}
          icon="🔐"
          tone="indigo"
        >
          <ClientPortalAccessSection
            patientId={patientId}
            patient={patient}
            edit={edit}
            setEdit={setEdit}
            subdomain={subdomain}
            newPassword={newPassword}
            setNewPassword={setNewPassword}
            onSaveEmail={onSavePortalEmail}
            onSetPassword={onSetPortalPassword}
            savingEmail={savingPortalEmail}
            settingPassword={settingPortalPassword}
            inFold={inFold}
          />
        </OverviewFold>

        <OverviewFold
          id="client-notes"
          title="Заметки"
          preview="история тренера"
          open={openId === "notes"}
          onToggle={() => toggle("notes")}
          icon="📝"
          tone="slate"
        >
          <ClientNotesSection
            patientId={patientId}
            legacyNote={patient?.internal_notes}
          />
        </OverviewFold>
      </div>
    </div>
  );
}

export default function ClientOverviewSection({
  patientId,
  patient,
  profile,
  edit,
  setEdit,
  onSave,
  subdomain,
  newPassword,
  setNewPassword,
  onSavePortalEmail,
  onSetPortalPassword,
  savingPortalEmail,
  settingPortalPassword,
  editingInfo,
  onToggleEdit,
  onAddToCompetition,
  compact = false,
  social = false,
}) {
  if (compact || social) {
    return (
      <ClientOverviewApp
        patientId={patientId}
        patient={patient}
        profile={profile}
        edit={edit}
        setEdit={setEdit}
        onSave={onSave}
        subdomain={subdomain}
        newPassword={newPassword}
        setNewPassword={setNewPassword}
        onSavePortalEmail={onSavePortalEmail}
        onSetPortalPassword={onSetPortalPassword}
        savingPortalEmail={savingPortalEmail}
        settingPortalPassword={settingPortalPassword}
        editingInfo={editingInfo}
        onToggleEdit={onToggleEdit}
        onAddToCompetition={onAddToCompetition}
        inFold={social}
      />
    );
  }

  const groups = profile?.groups || [];
  const individual = profile?.individual_sessions || [];
  const competitions = profile?.competitions || [];

  return (
    <div className={s.clientProfileTabPanel}>
      <div className={s.clientTabCard}>
        <div className={s.clientProfileEmbeddedHead}>
          <h3 className={s.clientTabCardTitle}>Группы</h3>
          <Link to="/manage/app/groups" className={s.clientProfileInlineLink}>
            Все группы
          </Link>
        </div>
        {groups.length === 0 ? (
          <EmptyState title="Не в группах" description="Зачислите клиента в группу в разделе «Группы»" />
        ) : (
          <ul className={s.clientGroupList}>
            {groups.map((g) => (
              <li key={g.id} className={s.clientGroupListItem}>
                <Link to={`/manage/app/groups?group=${g.id}`} className={s.clientGroupLink}>
                  <strong>{g.name}</strong>
                </Link>
                {g.sport_direction ? (
                  <span className={s.clientTabMuted}>{g.sport_direction}</span>
                ) : null}
                {g.trainer_name ? (
                  <span className={s.clientTabMuted}>Тренер: {g.trainer_name}</span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className={s.clientTabCard}>
        <div className={s.clientProfileEmbeddedHead}>
          <h3 className={s.clientTabCardTitle}>Индивидуальные тренировки</h3>
          <Link
            to={`/manage/app/schedule?create=individual&patient=${patientId}`}
            className={s.clientProfileInlineLink}
          >
            Запланировать
          </Link>
        </div>
        {individual.length === 0 ? (
          <p className={s.clientTabMuted}>
            Нет запланированных индивидуальных занятий. Создайте в календаре — формат «Индивидуальная».
          </p>
        ) : (
          <ul className={s.clientOverviewSessionList}>
            {individual.map((sess) => (
              <li key={sess.id} className={s.clientOverviewSessionItem}>
                <Link
                  to={`/manage/app/schedule?session=${sess.id}`}
                  className={s.clientGroupLink}
                >
                  <strong>
                    {formatDateRu(sess.start_datetime, {
                      day: "numeric",
                      month: "short",
                    })}{" "}
                    {formatTimeRu(sess.start_datetime)}
                  </strong>
                </Link>
                {sess.trainer_name ? (
                  <span className={s.clientTabMuted}>{sess.trainer_name}</span>
                ) : null}
                {sess.branch_name ? (
                  <span className={s.clientTabMuted}>{sess.branch_name}</span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className={s.clientTabCard}>
        <div className={s.clientProfileEmbeddedHead}>
          <h3 className={s.clientTabCardTitle}>Соревнования</h3>
          <Link to="/manage/app/schedule?create=event" className={s.clientProfileInlineLink}>
            Создать событие
          </Link>
        </div>
        {competitions.length === 0 ? (
          <p className={s.clientTabMuted}>
            Заявок пока нет. Клиент может подать заявку в личном кабинете, тренер — добавить в список участников события.
          </p>
        ) : (
          <ul className={s.clientOverviewSessionList}>
            {competitions.map((c) => (
              <li key={c.id} className={s.clientOverviewSessionItem}>
                <strong>{c.session?.title || "Соревнование"}</strong>
                {c.session?.start_datetime ? (
                  <span className={s.clientTabMuted}>
                    {formatDateRu(c.session.start_datetime, {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </span>
                ) : null}
                <span className={s.clientCompetitionStatus}>
                  {COMP_STATUS[c.status] || c.status}
                </span>
                {c.session?.id ? (
                  <Link
                    to={`/manage/app/schedule?session=${c.session.id}`}
                    className={s.clientProfileInlineLink}
                  >
                    Список участников
                  </Link>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {onAddToCompetition ? (
          <Button type="button" size="sm" variant="secondary" onClick={onAddToCompetition}>
            Добавить на соревнование
          </Button>
        ) : null}
      </div>

      <ClientInfoSection
        patient={patient}
        edit={edit}
        setEdit={setEdit}
        onSave={onSave}
        editing={editingInfo}
        onToggleEdit={onToggleEdit}
        embedded
      />

      <ClientPortalAccessSection
        patientId={patientId}
        patient={patient}
        edit={edit}
        setEdit={setEdit}
        subdomain={subdomain}
        newPassword={newPassword}
        setNewPassword={setNewPassword}
        onSaveEmail={onSavePortalEmail}
        onSetPassword={onSetPortalPassword}
        savingEmail={savingPortalEmail}
        settingPassword={settingPortalPassword}
      />
    </div>
  );
}
